/**
 * Hearts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_motifs_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const heartsPack: ToilePack = {
    slug: 'hearts',
    category: 'motif',
    name: 'Hearts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 832},
    readings: [
        {key: 'single', label: 'Single heart', subject: 'A single symmetrical heart shape centred on a plain ground, slightly rounded lobes, a soft highlight, nothing else.'},
        {key: 'pattern', label: 'Heart pattern', subject: 'A scattered pattern of hearts in three sizes and three tones tumbling across a plain ground, some tilted, a few outlined only.'},
        {key: 'anatomical', label: 'Anatomical', subject: 'An anatomical human heart with its vessels drawn accurately, roses and leaves growing from the aorta, a few drops, a plain ground.'},
    ],
    styles: [
        {key: 'flat', label: 'Flat', style: 'flat vector illustration, bold simplified shapes, limited palette, white ground', palette: 'a single bold red on white, no gradients', framing: 'centred on a plain white background with generous padding around the shape'},
        {key: 'tattoo', label: 'Tattoo linework', style: 'fine-line tattoo design, black ink only, clean weighted outlines, dotwork shading, white ground', palette: 'black ink only, no other colour', framing: 'centred on a plain white background with a clear margin around the design'},
        {key: 'watercolour', label: 'Watercolour', style: 'soft watercolour illustration, gentle washes, white paper', palette: 'crimson and blush washes with a little sage green', framing: 'set on a plain white paper background, the wash fading toward a soft margin'},
    ],
    directions: ['two hearts intertwined', 'with a name inside', 'for a valentine card'],
    directionHint: 'try: two hearts intertwined',
    seo: {
        title: 'Heart art: single, pattern, anatomical in three styles',
        metaDescription: 'Nine heart illustrations — a single heart, a scattered pattern and an anatomical heart — in flat, tattoo linework and watercolour styles.',
        h1: 'Heart drawings: single, pattern, anatomical in three styles',
        tagline: 'Nine renders, three heart readings, three looks each — nursery to tattoo.',
        intro: 'The one shape that runs from nursery wall to tattoo counter: a single symmetrical heart, a tumbling pattern of them, and an anatomical heart with roses growing from the aorta. Each renders flat and bold, as fine tattoo linework, or in soft watercolour washes. Ask for two intertwined hearts or a name inside and the same three looks carry it.',
        useCases: [
            'a valentine card or gift tag',
            'a tattoo flash reference sheet',
            'a nursery or bedroom print',
            'a social media love-themed post',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each is composed as a clean square crop, suited to a card, a print or a tattoo reference.'},
            {q: 'Can I get two hearts or a name inside?', a: 'Yes — the direction field takes any variation, and the same three styles render it.'},
            {q: "What's the difference between the three styles?", a: 'Flat is a single bold red shape, tattoo linework is black-ink dotwork shading, and watercolour is a soft crimson-and-blush wash.'},
            {q: 'Is the anatomical heart medically accurate?', a: "It's drawn to read as a real heart's vessels, styled for print rather than as a clinical diagram."},
        ],
    },
}
